'use client';

import { useRouter } from 'next/navigation';

import { useCurrentUserId } from '@ps/ui/contexts/auth';

/**
 * Footer under the sticky CTA. Mirrors RN's `GuestLoginFooter`, which is a
 * checkout-context block for anonymous/logged-out visitors:
 *
 *  - "Login for Faster Checkout (Optional)" — RN's `GuestSignInButton`
 *    (`if (!isAnonymousUser) return null`). Shown for non-external listings
 *    (`showLogin`); opens the login modal via the root `/login` route (works
 *    over both the bio and editor surfaces, like the PDP drawer itself).
 *  - The Google reCAPTCHA claim — RN's `GoogleReCAPTCHAClaim`. Static text +
 *    the Privacy Policy / Terms links.
 *
 * The WHOLE footer is hidden once the visitor is signed in — `useCurrentUserId`
 * resolves to a user id, so there's no guest-login prompt and no reCAPTCHA
 * claim (the claim is part of the guest-auth surface).
 */
export function CtaFooter({ showLogin }: { showLogin: boolean }) {
  const router = useRouter();
  // `useCurrentUserId` returns a string when signed in, `null` when resolved-
  // anonymous, and `undefined` while auth is still resolving (firebase loads
  // lazily). The footer is for logged-out visitors only (RN parity) — render it
  // ONLY once auth has resolved to `null`, so a signed-in visitor never flashes
  // the guest login prompt during the brief resolving window.
  const currentUserId = useCurrentUserId();
  if (currentUserId !== null) return null;

  return (
    <div className="mt-2 space-y-1.5 text-center">
      {showLogin ? (
        <button
          type="button"
          onClick={() => router.push('/login')}
          className="text-label font-medium text-zinc-500 underline"
        >
          Login for Faster Checkout (Optional)
        </button>
      ) : null}
      <p className="text-label leading-4 text-zinc-500">
        This site is protected by reCAPTCHA and the Google{' '}
        <a
          href="https://policies.google.com/privacy"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 underline"
        >
          Privacy Policy
        </a>{' '}
        and{' '}
        <a
          href="https://policies.google.com/terms"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 underline"
        >
          Terms of Service
        </a>{' '}
        apply.
      </p>
    </div>
  );
}
